import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { approvalService, type ApprovalStats, type GroupedTimesheets } from '@services/approvalService'
import { userService } from '@services/userService'
import { 
  CheckSquare, 
  Clock, 
  DollarSign, 
  AlertTriangle,
  Search,
  Filter,
  Eye,
  Calendar,
  Trash2
} from 'lucide-react'
import toast from 'react-hot-toast'

interface User {
  id: string
  name: string
  email: string
}

const ApprovalsPage: React.FC = () => {
  const { user } = useAuth()
  const [stats, setStats] = useState<ApprovalStats | null>(null)
  const [groupedTimesheets, setGroupedTimesheets] = useState<GroupedTimesheets | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    status: 'SUBMITTED',
    userId: '',
    search: '',
    page: 1,
    limit: 20
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteTimesheet, setDeleteTimesheet] = useState<any>(null)
  const [deleting, setDeleting] = useState(false)

  // 檢查權限
  if (!user || (user.role !== 'ADMIN' && user.role !== 'MANAGER')) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-800 mb-2">權限不足</h2>
          <p className="text-secondary-600">只有管理員和經理可以訪問審核功能</p>
        </div>
      </div>
    )
  }

  // 載入數據
  useEffect(() => {
    loadData()
    loadUsers()
  }, [filters])

  const loadData = async () => {
    try {
      setLoading(true)
      const [statsResponse, approvalsResponse] = await Promise.all([
        approvalService.getApprovalStats(),
        approvalService.getPendingApprovals(filters)
      ])

      if (statsResponse.success) {
        setStats(statsResponse.data!)
      }

      if (approvalsResponse.success) {
        setGroupedTimesheets(approvalsResponse.data!)
      }
    } catch (error) {
      console.error('Load data error:', error)
      toast.error('載入數據失敗')
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {
    try {
      const users = await userService.getUsers({ isActive: true })
      setUsers(users || [])
    } catch (error) {
      console.error('Load users error:', error)
    }
  }

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : (typeof value === 'number' ? value : parseInt(value.toString()) || 1)
    }))
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      SUBMITTED: { label: '🔄 待審核', className: 'bg-yellow-100 text-yellow-800' },
      APPROVED: { label: '✅ 已批准', className: 'bg-green-100 text-green-800' },
      REJECTED: { label: '❌ 已駁回', className: 'bg-red-100 text-red-800' },
      DRAFT: { label: '📝 草稿', className: 'bg-gray-100 text-gray-800' }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.DRAFT
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const formatDateRange = (weekKey: string) => {
    const [startDate, endDate] = weekKey.split('_')
    const start = new Date(startDate).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' })
    const end = new Date(endDate).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' })
    return `${start} - ${end}`
  }

  const calculateWeekTotal = (timesheets: any[]) => {
    return timesheets.reduce((sum, ts) => sum + (ts.totalAmount || 0), 0)
  }

  // 手機版工時申報卡片組件
  const TimesheetMobileCard: React.FC<{ timesheet: any; weekTotal: number }> = ({ timesheet, weekTotal }) => {
    const percentage = weekTotal > 0 ? ((timesheet.totalAmount || 0) / weekTotal * 100).toFixed(1) : '0.0'
    
    return (
      <div className="bg-white rounded-xl shadow-soft border border-primary-200 p-4">
        {/* 員工資訊頂部 */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-medium text-secondary-900">{timesheet.user?.name}</h4>
            <p className="text-sm text-secondary-500">{timesheet.user?.email}</p>
          </div>
          {getStatusBadge(timesheet.status)}
        </div>

        {/* 數據網格 - 3x2 */}
        <div className="grid grid-cols-3 gap-3 mb-3 text-center">
          <div>
            <p className="text-lg font-bold text-secondary-800">{timesheet.totalHours.toFixed(2)}h</p>
            <p className="text-xs text-secondary-600">工時</p>
          </div>
          <div>
            <p className="text-lg font-bold text-secondary-800">{timesheet.projectCount || 0}</p>
            <p className="text-xs text-secondary-600">專案數</p>
          </div>
          <div>
            <p className="text-lg font-bold text-secondary-800">{formatCurrency(timesheet.totalAmount || 0)}</p>
            <p className="text-xs text-secondary-600">金額</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div>
            <p className="text-sm font-medium text-secondary-700">
              {timesheet.submittedAt ? new Date(timesheet.submittedAt).toLocaleDateString('zh-TW') : '-'}
            </p>
            <p className="text-xs text-secondary-600">提交日期</p>
          </div>
          <div>
            <p className="text-sm font-medium text-secondary-700">{percentage}%</p>
            <p className="text-xs text-secondary-600">佔比</p>
          </div>
          <div>
            {/* 空格用於對齊 */}
          </div>
        </div>

        {/* 操作按鈕 */}
        <div className="flex gap-2 pt-3 border-t border-secondary-200">
          {timesheet.status === 'SUBMITTED' ? (
            <Link
              to={`/approvals/${timesheet.id}`}
              className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
            >
              <Eye className="w-4 h-4 mr-1" />
              審核
            </Link>
          ) : (
            <Link
              to={`/approvals/${timesheet.id}`}
              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-secondary-300 text-secondary-700 text-sm font-medium rounded-md hover:bg-secondary-50"
            >
              <Eye className="w-4 h-4 mr-1" />
              已完成
            </Link>
          )}
          
          {/* 刪除按鈕 */}
          {user?.role === 'ADMIN' && (timesheet.status === 'SUBMITTED' || timesheet.status === 'APPROVED') && (
            <button
              onClick={() => handleDeleteClick(timesheet)}
              className="px-3 py-2 border border-red-300 text-red-700 text-sm font-medium rounded-md hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    )
  }

  const handleDeleteClick = (timesheet: any) => {
    setDeleteTimesheet(timesheet)
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!deleteTimesheet) return

    try {
      setDeleting(true)
      const response = await approvalService.deleteTimesheet(deleteTimesheet.id)
      
      if (response.success) {
        toast.success('工時申報已刪除，相關工時已回復為未提交狀態')
        setShowDeleteModal(false)
        setDeleteTimesheet(null)
        // 重新載入數據
        loadData()
      } else {
        toast.error('刪除失敗')
      }
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('刪除失敗')
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-800 flex items-center gap-2">
            <CheckSquare className="w-7 h-7 text-primary-600" />
            工時審核
          </h1>
          <p className="text-secondary-600 mt-1">管理和審核員工工時申報</p>
        </div>
      </div>

      {/* 統計概覽 - 響應式網格 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          <div className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-secondary-600">總申報工時</p>
                <p className="text-xl md:text-2xl font-bold text-secondary-800">{stats.totalHours.toFixed(2)}h</p>
              </div>
              <Clock className="w-6 h-6 md:w-8 md:h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-secondary-600">總申報金額</p>
                <p className="text-xl md:text-2xl font-bold text-secondary-800">{formatCurrency(stats.totalAmount)}</p>
              </div>
              <DollarSign className="w-6 h-6 md:w-8 md:h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-secondary-600">待審核份數</p>
                <p className="text-xl md:text-2xl font-bold text-secondary-800">{stats.pendingCount}份</p>
              </div>
              <CheckSquare className="w-6 h-6 md:w-8 md:h-8 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-3 md:p-6 border border-primary-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm font-medium text-secondary-600">總逾期份數</p>
                <p className="text-xl md:text-2xl font-bold text-secondary-800">{stats.overdueCount}份</p>
              </div>
              <AlertTriangle className="w-6 h-6 md:w-8 md:h-8 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* 篩選器 - 手機版優化 */}
      <div className="bg-white rounded-xl shadow-soft p-4 border border-primary-200">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-primary-600" />
          <span className="text-sm font-medium text-secondary-700">篩選與搜尋</span>
        </div>
        
        {/* 狀態和用戶篩選 - 兩欄並排 */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-secondary-600 mb-1">
              狀態篩選
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-2 py-2 text-sm border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">全部狀態</option>
              <option value="SUBMITTED">待審核</option>
              <option value="APPROVED">已批准</option>
              <option value="REJECTED">已駁回</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-secondary-600 mb-1">
              用戶篩選
            </label>
            <select
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              className="w-full px-2 py-2 text-sm border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">全部用戶</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 搜尋框 - 獨立一行 */}
        <div>
          <label className="block text-xs font-medium text-secondary-600 mb-1">
            搜尋
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="搜尋員工姓名、信箱或專案名稱..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* 申報列表（按週期分組） */}
      <div className="space-y-6">
        {groupedTimesheets && Object.keys(groupedTimesheets.grouped).length > 0 ? (
          Object.entries(groupedTimesheets.grouped).map(([weekKey, timesheets]) => (
            <div key={weekKey} className="bg-white rounded-xl shadow-soft border border-primary-200">
              {/* 週期標題 */}
              <div className="px-6 py-4 border-b border-secondary-200 bg-secondary-50 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-secondary-800">
                      申報週期: {formatDateRange(weekKey)}
                    </h3>
                  </div>
                  <div className="text-sm font-medium text-secondary-600">
                    本期總金額: {formatCurrency(calculateWeekTotal(timesheets))}
                  </div>
                </div>
              </div>

              {/* 桌面版表格 */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">員工</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">工時</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">專案數</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">金額</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">狀態</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">提交</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">佔比</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">操作</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {timesheets.map((timesheet: any) => {
                      const weekTotal = calculateWeekTotal(timesheets)
                      const percentage = weekTotal > 0 ? ((timesheet.totalAmount || 0) / weekTotal * 100).toFixed(1) : '0.0'
                      
                      return (
                        <tr key={timesheet.id} className="hover:bg-secondary-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-secondary-900">{timesheet.user?.name}</div>
                              <div className="text-sm text-secondary-500">{timesheet.user?.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                            {timesheet.totalHours.toFixed(2)}h
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                            {timesheet.projectCount || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                            {formatCurrency(timesheet.totalAmount || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(timesheet.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                            {timesheet.submittedAt ? new Date(timesheet.submittedAt).toLocaleDateString('zh-TW') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                            {percentage}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              {timesheet.status === 'SUBMITTED' ? (
                                <Link
                                  to={`/approvals/${timesheet.id}`}
                                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  審核
                                </Link>
                              ) : (
                                <Link
                                  to={`/approvals/${timesheet.id}`}
                                  className="inline-flex items-center px-3 py-1 border border-secondary-300 text-sm leading-4 font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  已完成
                                </Link>
                              )}
                              {/* 刪除按鈕 - 只有管理員可以刪除已提交或已批准的申報 */}
                              {user.role === 'ADMIN' && (timesheet.status === 'SUBMITTED' || timesheet.status === 'APPROVED') && (
                                <button
                                  onClick={() => handleDeleteClick(timesheet)}
                                  className="inline-flex items-center px-2 py-1 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                  title="刪除工時申報"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* 手機版卡片 */}
              <div className="md:hidden p-4 space-y-3">
                {timesheets.map((timesheet: any) => (
                  <TimesheetMobileCard 
                    key={timesheet.id} 
                    timesheet={timesheet} 
                    weekTotal={calculateWeekTotal(timesheets)}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-soft p-12 text-center border border-primary-200">
            <CheckSquare className="w-16 h-16 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">暫無申報記錄</h3>
            <p className="text-secondary-500">目前沒有符合篩選條件的工時申報</p>
          </div>
        )}
      </div>

      {/* 刪除確認彈窗 */}
      {showDeleteModal && deleteTimesheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold text-secondary-800">刪除工時申報</h3>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-secondary-600 mb-2">
                您確定要刪除以下工時申報嗎？
              </p>
              <div className="bg-secondary-50 rounded-lg p-3">
                <p className="text-sm"><strong>員工：</strong>{deleteTimesheet.user?.name}</p>
                <p className="text-sm"><strong>週期：</strong>{formatDateRange(`${deleteTimesheet.weekStart.split('T')[0]}_${deleteTimesheet.weekEnd.split('T')[0]}`)}</p>
                <p className="text-sm"><strong>工時：</strong>{deleteTimesheet.totalHours}h</p>
                <p className="text-sm"><strong>金額：</strong>{formatCurrency(deleteTimesheet.totalAmount || 0)}</p>
                <p className="text-sm"><strong>狀態：</strong>{getStatusBadge(deleteTimesheet.status)}</p>
              </div>
            </div>

            <div className="mb-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">注意事項</span>
                </div>
                <ul className="text-xs text-yellow-700 space-y-1">
                  <li>• 刪除後，相關的工時記錄將回復為「未提交」狀態</li>
                  <li>• 員工可以重新修改和提交這些工時</li>
                  <li>• 此操作無法撤銷</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleting ? '刪除中...' : '確認刪除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ApprovalsPage
